import { useQueryClient } from "@tanstack/react-query";
import { isArray } from "lodash";
import { memo, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AsyncSelectValue } from "../../../../../../../components/AsyncSelect";
// Specialty filter removed from ROS import — CustomSelect no longer needed
// import CustomSelect from "../../../../../../../components/CustomSelect";
import { FlexCenterWrapper } from "../../../../../../../components/DefaultNoDataWrapper";
import Flex from "../../../../../../../components/Flex";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useRosPeTemplateCreate } from "../../../../../../../queries/encounter/use-ros-pe-template-create";
import { useRosPeTemplateUpdate } from "../../../../../../../queries/encounter/use-ros-pe-template-update";
import { useRosTemplateById } from "../../../../../../../queries/template/new-flow/use-new-template-by-id-list";
import { useNewTemplateList } from "../../../../../../../queries/template/new-flow/use-new-template-list";
// Specialty filter removed from ROS import
// import { useSpecialtiesList } from "../../../../../../../queries/use-specialities-list";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";
import RosTemplateDrawer from "../../../../../admin/library/templates/RosTemplateDrawer";
import TemplateSelectRow from "./components/TemplateSelectRow";
import RosPeImportList from "./components/RosPeImportList";
import {
  HOME_CARE_ROS_TEMPLATE_TYPE,
  ROS_IMPORT_TEMPLATE_TYPES,
} from "./home-care/home-care-constant";
import { isHomeCareItem } from "./home-care/home-care-utils";
import { useImportItems } from "./hooks/use-import-items";
interface RosImportDrawerProps {
  disclosureHost: DisclosureHost;
}

const RosImportDrawer = memo(({ disclosureHost }: RosImportDrawerProps) => {
  const { mutateAsync: createRosTemplate } = useRosPeTemplateCreate();
  const { mutateAsync: updateRosTemplate } = useRosPeTemplateUpdate();
  const appointmentId = useQueryParams("appointmentid");
  const { patientId: routePatientId } = useParams();
  const queryPatientId = useQueryParams("patientid");
  const patientId = queryPatientId || routePatientId;

  const {
    items: ros,
    setItems: setRos,
    handleSelect,
    onDescriptionChange,
  } = useImportItems();
  const [selectedTemplate, setSelectedTemplate] =
    useState<AsyncSelectValue | null>();
  // Specialty filter removed from ROS import
  // const [speciality, setSpeciality] = useState<string | null>();
  const [searchKey, setSearchKey] = useDebounce<string>("");
  const [templatePagination, setTemplatePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  // const [specialitySearch, setSpecialitySearch] = useDebounce<string>("");
  const showSnackbar = useSnackbar();
  const rosTemplateDisc = useDisclosure();
  const queryClient = useQueryClient();

  // const { data: specialities } = useSpecialtiesList({
  //   searchString: specialitySearch,
  //   xTENANTID: getXtenantId(),
  // });

  // const specialtiesList = useMemo(
  //   () =>
  //     specialities?.data?.content?.map((it) => ({
  //       value: it?.uuid,
  //       label: it?.name,
  //     })),
  //   [specialities]
  // );

  const { data, isLoading, isRefetching } = useNewTemplateList({
    enabled: disclosureHost.opened,
    pagination: templatePagination,
    gridSorting: [],
    searchKey,
    templateType: ROS_IMPORT_TEMPLATE_TYPES,
    // specialityUuids: speciality ? [speciality] : undefined,
    xTENANTID: getXtenantId(),
  });

  const rosTemplateList = useMemo(() => {
    return data?.data?.content?.map((it) => ({
      value: it.uuid,
      label: it?.templateName,
    }));
  }, [data]);

  const { data: templateByIdData } = useRosTemplateById({
    rosTemplateUuid: selectedTemplate?.value,
    xTENANTID: getXtenantId(),
  });

  const isHomeCareTemplate =
    !!ros?.length &&
    ((!!selectedTemplate?.value &&
      data?.data?.content?.find((it) => it.uuid === selectedTemplate.value)
        ?.templateTypes === HOME_CARE_ROS_TEMPLATE_TYPE) ||
      (!selectedTemplate?.value && ros.every(isHomeCareItem)));

  useEffect(() => {
    if (selectedTemplate?.value) {
      setRos(
        templateByIdData?.data?.rosTemplateTypeSet?.map((it: any) => ({
          templateType: "ROS",
          templateNameType: it?.name,
          description: it?.value,
          isSelected: it?.isSelected,
        })) ?? null
      );
    } else if (disclosureHost?.data && isArray(disclosureHost.data)) {
      setRos(disclosureHost?.data?.map((it) => ({ ...it, isSelected: true })));
    } else if (disclosureHost?.data) {
      setRos([{ ...disclosureHost?.data, isSelected: true }]);
    }
  }, [disclosureHost.opened, templateByIdData]);

  const handleSubmit = () => {
    const payload = {
      physicalROSTemplateRequest: {
        appointmentId: appointmentId ?? undefined,
        patientId: patientId ?? undefined,
        physicalROSTemplateList: ros?.filter((it) => !!it.isSelected),
      },
    };

    (disclosureHost?.data || !isArray(disclosureHost?.data)
      ? updateRosTemplate(payload)
      : createRosTemplate(payload)
    )
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({
          queryKey: ["patient-ros-pe-template-list"],
        });
        closeAndReset();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const closeAndReset = () => {
    setRos(null);
    setSelectedTemplate(null);
    // setSpeciality(null);
    disclosureHost.close();
  };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndReset}
      dialogTitle="Add ROS"
      dialogAction={
        <Flex gap={1}>
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={handleSubmit}
          />
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={closeAndReset}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "65vh",
        },
      }}
    >
      <Flex flexDirection={"column"} gap={1} width={"100%"} pt={2}>
        {(isArray(disclosureHost.data) || !disclosureHost?.data) && (
          <>
            <TemplateSelectRow
              isLoading={isLoading || isRefetching}
              value={selectedTemplate}
              options={rosTemplateList}
              onSearch={(v) => {
                setSearchKey(v);
                setTemplatePagination((p) => ({ ...p, page: 0 }));
              }}
              onChange={setSelectedTemplate}
              onAdd={() => rosTemplateDisc.open()}
              pagination={templatePagination}
              setPagination={setTemplatePagination}
              totalElements={data?.data?.page?.totalElements}
            />
            <RosTemplateDrawer disclosureHost={rosTemplateDisc} />
          </>
        )}

        {((!ros?.length && !disclosureHost?.data) ||
          (isArray(disclosureHost?.data) &&
            !disclosureHost?.data?.length &&
            !ros?.length)) && (
          <Flex height={"60vh"}>
            <FlexCenterWrapper>{"Please Select Template"}</FlexCenterWrapper>
          </Flex>
        )}

        {ros && (
          <RosPeImportList
            items={ros}
            isHomeCare={isHomeCareTemplate}
            onSelect={handleSelect}
            onDescriptionChange={onDescriptionChange}
          />
        )}
      </Flex>
    </StyledFormModal>
  );
});

export default RosImportDrawer;
